import {
    CERTAIN_LIKELIHOOD_DESCRIPTION,
    CERTAIN_LIKELIHOOD_RATING,
    CLASSI_WEIGHTING,
    CLASSII_WEIGHTING,
    CLASSIII_WEIGHTING,
    CLASSIV_WEIGHTING,
    HARM_CLASSES,
    LESS_THAN_AVERAGE,
    LIKELIHOOD_SCALE_DESCRIPTIONS,
    LIKELIHOOD_SCALE_POINTS,
    SPREAD_OF_HARM_TOTAL,
} from '../../constants/HHSRS';

const extractClassRatingsFromFormValues = (formValues) => {
    const ratings = {};

    HARM_CLASSES.forEach((harmClass) => {
        const { fieldName } = harmClass;

        if (!(fieldName in formValues)) {
            throw new Error(`${fieldName} not found in form values.`);
        }
        ratings[fieldName] = formValues[fieldName];
    });

    return ratings;
};

export const isValidCalculation = (formValues) => {
    const ratings = extractClassRatingsFromFormValues(formValues);

    const ratingTotal = Number(
        HARM_CLASSES.reduce(
            (accumulated, current) =>
                Number(ratings[current.fieldName]) + accumulated,
            0
        )
    );

    return Number(ratingTotal.toFixed(1)) === SPREAD_OF_HARM_TOTAL;
};

export const calculateMostLikelyRating = (formValues, fieldName) => {
    // Get all ratings apart from the current harm class.
    const ratings = Object.keys(
        extractClassRatingsFromFormValues(formValues)
    ).filter((aFieldName) => aFieldName !== fieldName);

    const accumulatedRatings = ratings.reduce(
        (accumulated, current) => Number(formValues[current]) + accumulated,
        0
    );

    const calculatedRating = Number(SPREAD_OF_HARM_TOTAL - accumulatedRatings);
    const currentRating = Number(formValues[fieldName]);

    if (calculatedRating < 0) {
        return currentRating;
    }
    return calculatedRating.toFixed(1);
};

export const getMostLikelyHarmFieldName = (formValues) => {
    const harmRatings = HARM_CLASSES.map(
        (harmClass) => harmClass.fieldName
    ).map((ratingName) => ({
        name: ratingName,
        value: Number(formValues[ratingName]),
    }));
    return harmRatings.sort((a, b) => b.value - a.value)[0].name;
};

export const getLikelihoodScalePoints = () =>
    LIKELIHOOD_SCALE_POINTS;

export const getLikelihoodScaleDescription = (scalePoints, likelihood, nationalAverage) => {

    if (likelihood > nationalAverage) {
        return LESS_THAN_AVERAGE;
    }

    if (likelihood === CERTAIN_LIKELIHOOD_RATING) {
        return CERTAIN_LIKELIHOOD_DESCRIPTION;
    }

    const bandSize = scalePoints.length / LIKELIHOOD_SCALE_DESCRIPTIONS.length;

    return LIKELIHOOD_SCALE_DESCRIPTIONS[
        Math.round(scalePoints.indexOf(likelihood) / bandSize)
    ];
};

export const getLikelihoodStartFromScalePoints = (nationalAverage) => {
    switch (true) {
        case nationalAverage > 4200:
            return 5600;
        case nationalAverage > 2400:
            return 3200;
        case nationalAverage > 1300:
            return 1800;
        case nationalAverage > 750:
            return 1000;
        case nationalAverage > 420:
            return 560;
        case nationalAverage > 240:
            return 320;
        case nationalAverage > 130:
            return 180;
        case nationalAverage > 75:
            return 100;
        case nationalAverage > 42:
            return 56;
        case nationalAverage > 24:
            return 32;
        case nationalAverage > 13:
            return 18;
        case nationalAverage > 7.5:
            return 10;
        case nationalAverage > 4:
            return 6;
        case nationalAverage > 2.5:
            return 3;
        case nationalAverage > 1.5:
            return 2;
        default:
            return 1;
    }
};

export const getBandForScore = (score) => {
    switch (true) {
        case score <= 9:
            return 'J';
        case score <= 19:
            return 'I';
        case score <= 49:
            return 'H';
        case score <= 99:
            return 'G';
        case score <= 199:
            return 'F';
        case score <= 499:
            return 'E';
        case score <= 999:
            return 'D';
        case score <= 1999:
            return 'C';
        case score <= 4999:
            return 'B';
        default:
            return 'A';
    }
};

export const getCalculatedScore = (formValues) => {
    const classIScore =
        CLASSI_WEIGHTING *
        (1 / formValues.likelihood) *
        formValues.classi_rating;

    const classIIScore =
        CLASSII_WEIGHTING *
        (1 / formValues.likelihood) *
        formValues.classii_rating;

    const classIIIScore =
        CLASSIII_WEIGHTING *
        (1 / formValues.likelihood) *
        formValues.classiii_rating;

    const classIVScore =
        CLASSIV_WEIGHTING *
        (1 / formValues.likelihood) *
        formValues.classiv_rating;

    const score = classIScore + classIIScore + classIIIScore + classIVScore;
    const band = getBandForScore(score);

    return {
        score,
        band,
    };
};
